/**
 * Description tab — clinical contract for the variable, rendered as prose.
 *
 * The opening prose comes from ``artifact.description``. Below it we
 * concatenate five ``clinicalExplanation`` fields — answers, qualifyingCriteria,
 * timeWindow, valueSelection, missingValues — into one or two flowing paragraphs
 * separated by blank lines, matching the Figma "Description" section design.
 *
 * Backwards-compat: pre-existing artifacts whose payload still carried
 * ``explainMarkdown`` fall through to that body when no structured explanation
 * is present.
 */

import { Box, Stack, Typography } from '@mui/material';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';

import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import type {
  EpiArtifact,
  EpiArtifactOutputType,
  EpiClinicalExplanation,
} from '../../types/epi-chat-types';
import { Markdown } from '../Markdown';
import { ArtifactTabCard } from './ArtifactTabCard';
import { ConceptGroupIdFallback } from './ConceptGroupIdFallback';
import { ConceptMappingsTable } from './ConceptMappingsTable';
import { UsedInSection } from './UsedInSection';

const OUTPUT_TYPE_VALUES_KEY: Record<EpiArtifactOutputType, string> = {
  boolean: 'epiChat.descriptionTab.outputValueType.boolean',
  string: 'epiChat.descriptionTab.outputValueType.string',
  date: 'epiChat.descriptionTab.outputValueType.date',
  numeric: 'epiChat.descriptionTab.outputType.numeric',
};

const PROSE_KEYS: (keyof EpiClinicalExplanation)[] = [
  'answers',
  'qualifyingCriteria',
  'timeWindow',
  'valueSelection',
  'missingValues',
];

const CATEGORICAL_MAX = 12;

/** Observed categories from the latest execution's string distribution — only when it looks
 *  categorical (bounded distinct count, no large "other" bucket). Frequency-ordered. */
const observedCategories = (artifact: EpiArtifact): string[] | undefined => {
  const dist = artifact.executions?.[0]?.distribution;
  if (!dist || dist.kind !== 'string') return undefined;
  if ((dist.otherValueCount ?? 0) > 0) return undefined;
  const top = dist.topValues ?? [];
  if (top.length === 0 || top.length > CATEGORICAL_MAX) return undefined;
  return top.map((t) => t.value);
};

/** Resolve the "Values" field per OR-6266 (resolution order: boolean/date deterministic,
 *  then the RA-authored `outputValues`, then observed pass-through categories, then the
 *  legacy generic type label). */
const resolveValuesDisplay = (
  artifact: EpiArtifact,
  formatMessage: IntlShape['formatMessage']
): string => {
  if (artifact.format === 'boolean')
    return formatMessage({ id: 'epiChat.descriptionTab.values.boolean' });
  if (artifact.format === 'date')
    return formatMessage({ id: 'epiChat.descriptionTab.values.date' });

  const drafted = artifact.outputValues?.trim();
  if (drafted) return drafted;

  if (artifact.format === 'string') {
    const observed = observedCategories(artifact);
    if (observed?.length) return observed.join('; ');
  }

  if (artifact.outputValueType)
    return formatMessage({
      id: `epiChat.descriptionTab.outputValueType.${artifact.outputValueType}`,
    });
  return artifact.format ? formatMessage({ id: OUTPUT_TYPE_VALUES_KEY[artifact.format] }) : '';
};

export const DescriptionTab = ({
  artifact,
  handleCloseDialog,
}: {
  artifact: EpiArtifact;
  handleCloseDialog?: () => void;
}) => {
  const { formatMessage } = useIntl();
  const explanation = artifact.clinicalExplanation;
  const conceptMappings = artifact.conceptMappings ?? [];
  const conceptGroupIds = artifact.conceptGroupIds ?? [];
  const showConceptMappings = conceptMappings.length > 0;
  // Bug 6: when the structured mappings are empty but the artifact still carries
  // concept-group ids (mid-draft / placeholder binding), fall back to resolving
  // those ids so the section isn't silently hidden.
  const showConceptGroupIdFallback = conceptMappings.length === 0 && conceptGroupIds.length > 0;

  // timeWindow moves into the resolved "Values" field, so drop it from the prose block.
  const proseKeys = PROSE_KEYS.filter((k) => k !== 'timeWindow');

  const proseBody = explanation
    ? proseKeys
        .map((k) => explanation[k])
        .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
        .join('\n\n')
    : '';

  if (
    !artifact.description &&
    proseBody.length === 0 &&
    !artifact.explainMarkdown &&
    !artifact.name &&
    !artifact.format &&
    !showConceptMappings &&
    !showConceptGroupIdFallback
  ) {
    return (
      <ArtifactTabCard>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'epiChat.descriptionTab.empty' })}
        </Typography>
      </ArtifactTabCard>
    );
  }

  const descriptionContent = proseBody.length > 0 ? proseBody : artifact.description ?? '';

  return (
    <ArtifactTabCard data-testid="artifact-description-tab">
      {artifact.name && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          data-testid="artifact-alias-section"
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatMessage({ id: 'epiChat.descriptionTab.section.alias' })}
          </Typography>
          <Typography variant="body1">{artifact.name}</Typography>
        </Box>
      )}
      {descriptionContent.length > 0 && (
        <Box
          sx={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 4 }}
          data-testid="artifact-description-section"
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatMessage({ id: 'epiChat.descriptionTab.section.description' })}
          </Typography>
          <Box data-testid="artifact-description-prose">
            <Markdown sxProps={{ fontSize: fontSize[16] }}>{descriptionContent}</Markdown>
          </Box>
        </Box>
      )}
      {artifact.format && (
        <Box
          sx={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 4 }}
          data-testid="artifact-output-section"
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatMessage({ id: 'epiChat.descriptionTab.section.output' })}
          </Typography>
          <Stack direction="row" spacing={4}>
            {artifact.format && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 0.25 }}>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage({ id: 'epiChat.descriptionTab.label.format' })}
                </Typography>
                <Typography variant="body1">
                  {formatMessage({
                    id: `epiChat.descriptionTab.outputType.${artifact.format}`,
                  })}
                </Typography>
              </Box>
            )}
            {(artifact.outputValueType ?? artifact.format) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 0.25 }}>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage({ id: 'epiChat.descriptionTab.label.values' })}
                </Typography>
                <Typography variant="body1">
                  {resolveValuesDisplay(artifact, formatMessage)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}
      {showConceptMappings && (
        <Box sx={{ marginTop: 7 }} data-testid="artifact-concept-mappings">
          <ConceptMappingsTable mappings={conceptMappings} />
        </Box>
      )}
      {showConceptGroupIdFallback && (
        <Box sx={{ marginTop: 7 }} data-testid="artifact-concept-group-ids">
          <ConceptGroupIdFallback conceptGroupIds={conceptGroupIds} />
        </Box>
      )}
      {artifact.artifactStatus !== 'drafting' && (
        <Box sx={{ marginTop: 7 }} data-testid="artifact-used-in-section">
          <UsedInSection avId={artifact.id} handleCloseDialog={handleCloseDialog} />
        </Box>
      )}
    </ArtifactTabCard>
  );
};
